import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import ProjectPreOrderLayoutWrapper from "../../components/ProjectPreOrderLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSearch, FaChevronUp, FaChevronDown } from "react-icons/fa";

const API = "https://nissi-india.com/api/boqReport";

const pad2 = (n) => String(n).padStart(2, "0");
const toDDMMYYYY = (d) =>
  `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;

// yyyy-MM-dd (native <input type="date"> value) -> dd/MM/yyyy (backend format)
const isoToDDMMYYYY = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return "";
  return `${d}/${m}/${y}`;
};

// dd/MM/yyyy (backend format) -> yyyy-MM-dd (native <input type="date"> value)
const ddmmyyyyToISO = (ddmmyyyy) => {
  if (!ddmmyyyy) return "";
  const [d, m, y] = ddmmyyyy.split("/");
  if (!d || !m || !y) return "";
  return `${y}-${m}-${d}`;
};

const BOQReport = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const [showFilters, setShowFilters] = useState(true);

  const [formData, setFormData] = useState({
    fromDate: "",
    toDate: "",
    nameOfWork: "",
    boqCode: "",
  });

  const [loading, setLoading] = useState(false);

  // dropdown data
  const [workOptions, setWorkOptions] = useState([]);
  const [codeOptions, setCodeOptions] = useState([]);

  // report data
  const [reportData, setReportData] = useState([]);

  // localStorage helper
  const getLS = (key) => localStorage.getItem(key) || "";

  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branchType = getLS("branchType") || getLS("branchtype");
  const userId = getLS("userId") || getLS("loginuserid");

  // common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const loadInit = async () => {
    try {
      const res = await axios.get(`${API}/init`, {
        headers: commonHeaders,
      });

      const data = res.data?.data || {};
      setWorkOptions(data.projects || []);
      setCodeOptions(data.codes || []);
    } catch (err) {
      console.error("boqReport/init error:", err);
      toast.error(err?.response?.data?.message || "Failed to load dropdowns");
    }
  };

  const loadList = async (params) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/list`, params, {
        headers: commonHeaders,
      });

      const rows = res.data?.data || [];
      setReportData(rows);
    } catch (err) {
      console.error("boqReport/list error:", err);
      toast.error(err?.response?.data?.message || "Failed to load report");
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const today = new Date();
    const from = new Date();
    from.setDate(today.getDate() - 30);

    const initial = {
      fromDate: toDDMMYYYY(from),
      toDate: toDDMMYYYY(today),
      nameOfWork: "",
      boqCode: "",
    };

    setFormData(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!formData.fromDate || !formData.toDate) return;
    loadInit();
    loadList(formData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commonHeaders, formData.fromDate, formData.toDate]);

  const handleReset = () => {
    const today = new Date();
    const from = new Date();
    from.setDate(today.getDate() - 30);

    setFormData({
      fromDate: toDDMMYYYY(from),
      toDate: toDDMMYYYY(today),
      nameOfWork: "",
      boqCode: "",
    });
  };

  const handleSearch = () => {
    toast.info("Searching BOQ records...");
    loadList(formData);
  };

  const tableTitle = useMemo(() => "BOQ Report", []);

  const handlePrint = (row) => {
    window.open(
      `/OrderManagement/EstimationDetailsPrint_1?preEstimationId=${encodeURIComponent(
        row.preEstimationId
      )}`,
      "_blank",
      "width=900,height=900,scrollbars=yes"
    );
  };

  const handleExcel = (row) => {
    const url = new URL(`${API}/excel`);
    url.searchParams.append("preEstimationId", row.preEstimationId || "");
    url.searchParams.append("estimationId", row.estimationId || "");
    url.searchParams.append("finYearId", finYearId);
    url.searchParams.append("finYear", finYear);
    url.searchParams.append("branchId", branchId);
    url.searchParams.append("branchType", branchType);
    url.searchParams.append("userId", userId);

    window.open(url.toString(), "_blank");
  };

  const formatMoney = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return "";
    return n.toFixed(2);
  };

  return (
    <ProjectPreOrderLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">BOQ REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-4">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                {tableTitle}
              </h1>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {showFilters && (
            <div
              className={`p-6 rounded-xl border mb-6 ${
                isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
              }`}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                                       <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      From Date
                    </label>
                    <input
                      type="date"
                      value={ddmmyyyyToISO(formData.fromDate)}
                      onChange={(e) =>
                        handleInputChange("fromDate", isoToDDMMYYYY(e.target.value))
                      }
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                      }`}
                    />
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Name Of the Work
                    </label>
                    <select
                      value={formData.nameOfWork}
                      onChange={(e) => handleInputChange("nameOfWork", e.target.value)}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                    >
                      <option value="">Select</option>
                      {workOptions.map((x, index) => (
                        <option key={x.value || index} value={x.value}>
                          {x.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                                        <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      To Date
                    </label>
                    <input
                      type="date"
                      value={ddmmyyyyToISO(formData.toDate)}
                      onChange={(e) =>
                        handleInputChange("toDate", isoToDDMMYYYY(e.target.value))
                      }
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                      }`}
                    />
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      BOQ Code
                    </label>
                    <select
                      value={formData.boqCode}
                      onChange={(e) => handleInputChange("boqCode", e.target.value)}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                    >
                      <option value="">Select</option>
                      {codeOptions.map((x, index) => (
                        <option key={x.value || index} value={x.value}>
                          {x.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

                            <div className="flex justify-center items-center mt-6 gap-4">
                <button
                  onClick={handleSearch}
                  disabled={loading}
                  className={`flex items-center gap-2 px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                    loading ? "opacity-60 cursor-not-allowed" : ""
                  } ${buttonClass} ${hoverClass} text-white`}
                >
                  <FaSearch />
                  {loading ? "Searching..." : "Search"}
                </button>

                <button
                  onClick={handleReset}
                  disabled={loading}
                  className={`flex items-center gap-2 px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                    loading ? "opacity-60 cursor-not-allowed" : ""
                  } ${
                    isDark
                      ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                      : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                  }`}
                >
                  Reset
                </button>
              </div>
            </div>
          )}

          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-3 py-4 text-center font-semibold border-r border-white/20">S.No</th>
                    <th className="px-3 py-4 text-center font-semibold border-r border-white/20">BOQ Code</th>
                    <th className="px-3 py-4 text-center font-semibold border-r border-white/20">Name of the Project</th>
                    <th className="px-3 py-4 text-center font-semibold border-r border-white/20">Date</th>
                    <th className="px-3 py-4 text-center font-semibold border-r border-white/20">Tender Inviting Authority</th>
                    <th className="px-3 py-4 text-center font-semibold border-r border-white/20">Tender Package No</th>
                    <th className="px-3 py-4 text-center font-semibold border-r border-white/20">Bidder Name</th>
                    <th className="px-3 py-4 text-center font-semibold border-r border-white/20">Status</th>
                    <th className="px-3 py-4 text-center font-semibold border-r border-white/20">Total Value</th>
                    <th className="px-3 py-4 text-center font-semibold border-r border-white/20">Report</th>
                    <th className="px-3 py-4 text-center font-semibold">Excel</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    <tr className={isDark ? "bg-gray-800" : "bg-gray-50"}>
                      <td
                        colSpan={11}
                        className={`text-center py-8 text-lg font-medium ${
                          isDark ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        Loading...
                      </td>
                    </tr>
                  ) : reportData.length > 0 ? (
                    reportData.map((item, index) => (
                      <tr
                        key={item.estimationId || index}
                        className={`transition-colors duration-200 ${
                          isDark ? "bg-gray-800 hover:bg-gray-750" : "bg-white hover:bg-gray-50"
                        }`}
                      >
                        <td
                          className={`px-3 py-3 text-center border-r ${
                            isDark
                              ? "border-gray-700 text-white"
                              : "border-gray-200 text-gray-900"
                          }`}
                        >
                          {item.rowno ?? index + 1}
                        </td>

                        <td
                          className={`px-3 py-3 text-center border-r ${
                            isDark
                              ? "border-gray-700 text-white"
                              : "border-gray-200 text-gray-900"
                          }`}
                        >
                          {item.estimationCode}
                        </td>

                        <td
                          className={`px-3 py-3 text-center border-r ${
                            isDark
                              ? "border-gray-700 text-white"
                              : "border-gray-200 text-gray-900"
                          }`}
                        >
                          {item.projectName}
                        </td>

                        <td
                          className={`px-3 py-3 text-center border-r ${
                            isDark
                              ? "border-gray-700 text-white"
                              : "border-gray-200 text-gray-900"
                          }`}
                        >
                          {item.estimationDate}
                        </td>

                        <td
                          className={`px-3 py-3 text-center border-r ${
                            isDark
                              ? "border-gray-700 text-white"
                              : "border-gray-200 text-gray-900"
                          }`}
                        >
                          {item.customerName}
                        </td>

                        <td
                          className={`px-3 py-3 text-center border-r ${
                            isDark
                              ? "border-gray-700 text-white"
                              : "border-gray-200 text-gray-900"
                          }`}
                        >
                          {item.customerAddress}
                        </td>

                        <td
                          className={`px-3 py-3 text-center border-r ${
                            isDark
                              ? "border-gray-700 text-white"
                              : "border-gray-200 text-gray-900"
                          }`}
                        >
                          {item.remarks}
                        </td>

                        <td
                          className={`px-3 py-3 text-center border-r ${
                            isDark ? "border-gray-700" : "border-gray-200"
                          }`}
                        >
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              String(item.statusText || "").toLowerCase() === "approved"
                                ? isDark
                                  ? "bg-green-900/40 text-green-200"
                                  : "bg-green-100 text-green-700"
                                : isDark
                                ? "bg-yellow-900/40 text-yellow-200"
                                : "bg-yellow-100 text-yellow-700"
                            }`}
                          >
                            {item.statusText}
                          </span>
                        </td>

                        <td
                          className={`px-3 py-3 text-center border-r ${
                            isDark
                              ? "border-gray-700 text-white"
                              : "border-gray-200 text-gray-900"
                          }`}
                        >
                          {formatMoney(item.amount)}
                        </td>

                        <td
                          className={`px-3 py-3 text-center border-r ${
                            isDark ? "border-gray-700" : "border-gray-200"
                          }`}
                        >
                          <button
                            onClick={() => handlePrint(item)}
                            className={`underline text-xs ${
                              isDark
                                ? "text-blue-400 hover:text-blue-300"
                                : "text-blue-600 hover:text-blue-800"
                            }`}
                          >
                            Print
                          </button>
                        </td>

                        <td
                          className={`px-3 py-3 text-center ${
                            isDark ? "text-white" : "text-gray-900"
                          }`}
                        >
                          <button
                            onClick={() => handleExcel(item)}
                            className={`underline text-xs ${
                              isDark
                                ? "text-blue-400 hover:text-blue-300"
                                : "text-blue-600 hover:text-blue-800"
                            }`}
                          >
                            Excel
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr className={isDark ? "bg-gray-800" : "bg-gray-50"}>
                      <td
                        colSpan={11}
                        className={`text-center py-8 text-lg font-medium ${
                          isDark ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        No Records Found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </ProjectPreOrderLayoutWrapper>
  );
};

export default BOQReport;